import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import api from "../api/api.js";
import { Eye, EyeOff } from "lucide-react";
import styles from "./ResetPassword.module.css";
import { resetPasswordSchema, getPasswordStrength } from "../lib/validations";

const RestablecerContrasena = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [showNew, setShowNew]           = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  const [loading, setLoading]           = useState(false);
  const [message, setMessage]           = useState("");
  const [isError, setIsError]           = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const newPasswordValue = watch("newPassword", "");
  const strength = getPasswordStrength(newPasswordValue);

  const onSubmit = async (data) => {
    setMessage("");

    if (!token) {
      setIsError(true);
      setMessage("El enlace de recuperación no es válido o ha expirado. Solicita uno nuevo.");
      return;
    }

    try {
      setLoading(true);
      const { data: resp } = await api.post("/reset_password", {
        token,
        newPassword: data.newPassword,
      });
      setIsError(false);
      setMessage(typeof resp === 'string' ? resp : "Contraseña restablecida con éxito.");
      // Redirigir al login tras 2 segundos
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setIsError(true);
      setMessage(
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data ||
        "Error al restablecer la contraseña. El enlace puede haber expirado."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.background}>
      <div className={styles.container}>
        <h2 className={styles.title}>Restablecer Contraseña</h2>
        <p className={styles.subtitle}>
          Ingresa tu nueva contraseña. Debe tener al menos 8 caracteres, incluyendo
          mayúsculas, minúsculas, un número y un símbolo.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className={styles.form}>

          {/* NUEVA CONTRASEÑA */}
          <div className={styles.inputGroup}>
            <label htmlFor="newPassword" className={styles.label}>Nueva contraseña</label>
            <div className={styles.inputWrapper}>
              <input
                id="newPassword"
                type={showNew ? "text" : "password"}
                placeholder="Mínimo 8 caracteres"
                className={`${styles.input} ${errors.newPassword ? styles.inputError : ''}`}
                aria-describedby={errors.newPassword ? 'newPassword-error' : undefined}
                aria-invalid={errors.newPassword ? 'true' : undefined}
                {...register('newPassword')}
              />
              <button
                type="button"
                onClick={() => setShowNew(v => !v)}
                className={styles.iconButton}
                aria-label={showNew ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Barra de fortaleza */}
            {newPasswordValue && (
              <div className={styles.strengthContainer}>
                <div className={styles.strengthBar}>
                  <div
                    className={`${styles.strengthFill} ${styles[`strength_${strength?.level}`]}`}
                    style={{ width: `${strength?.percent || 0}%` }}
                  />
                </div>
                <span className={`${styles.strengthLabel} ${styles[`strengthText_${strength?.level}`]}`}>
                  {strength?.label}
                </span>
              </div>
            )}

            {errors.newPassword && (
              <span id="newPassword-error" className={styles.fieldError} role="alert">
                {errors.newPassword.message}
              </span>
            )}
          </div>

          {/* CONFIRMAR CONTRASEÑA */}
          <div className={styles.inputGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>Confirmar contraseña</label>
            <div className={styles.inputWrapper}>
              <input
                id="confirmPassword"
                type={showConfirm ? "text" : "password"}
                placeholder="Repite tu contraseña"
                onPaste={(e) => e.preventDefault()}
                className={`${styles.input} ${errors.confirmPassword ? styles.inputError : ''}`}
                aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                aria-invalid={errors.confirmPassword ? 'true' : undefined}
                {...register('confirmPassword')}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(v => !v)}
                className={styles.iconButton}
                aria-label={showConfirm ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <span id="confirmPassword-error" className={styles.fieldError} role="alert">
                {errors.confirmPassword.message}
              </span>
            )}
          </div>

          {message && (
            <p className={isError ? styles.messageError : styles.messageSuccess}>
              {message}
            </p>
          )}

          <button type="submit" disabled={loading} className={styles.button}>
            {loading ? "Procesando..." : "Restablecer contraseña"}
          </button>
        </form>

        <p className={styles.backLink}>
          <span onClick={() => navigate("/login")}>Volver al inicio de sesión</span>
        </p>
      </div>
    </div>
  );
};

export default RestablecerContrasena;
